import PropTypes from 'prop-types';
import { useEffect, useState } from 'react';
import { gql, useMutation } from '@apollo/client';
import { Field, Form, Formik } from 'formik';
import { toast } from 'react-toastify';
import * as Sentry from '@sentry/react';

// ours
import { CircledCheckmarkIcon } from '../../atoms/Icons';
import LabelledColumn from '../../atoms/LabelledColumn';
import Button from '../../atoms/Button';
import TextInput from '../../atoms/TextInput/TextInput';
import './CreateBarnForm.scss';

export default function CreateBarnForm({ className = '', orgId = '', refetch }) {
  // --- State hooks ---
  const [showSuccess, setShowSuccess] = useState(false);

  // --- Navigation/side effects ---
  useEffect(() => {
    let timerId = null;

    if (showSuccess) {
      setTimeout(() => {
        setShowSuccess(false);
        // switch back to button after 3.5s
      }, 3500);
    }

    // cleanup timer
    return () => clearTimeout(timerId);
  }, [showSuccess]);

  // set up the barn creation mutation
  const createBarnMutation = gql`
    mutation CreateBarnForm_InsertFarm_Name($newRowObject: farm_insert_input!) {
      new_farm: insert_farm_one(object: $newRowObject) {
        id
        name
      }
    }
  `;

  // --- Apollo/GraphQL hooks ---
  const [saveBarn, { loading: savingBarn, error }] = useMutation(createBarnMutation, {
    refetchQueries: ['Access_Control__Privileges', 'CommandPalette_GetBarns'], // make sure privileges and cmdk include the new barn
  });

  // --- Function definitions ---
  const handleOnSuccess = (barnName, barnId) =>
    toast.success(
      <div className="CreateBarnForm-successToast">
        <span className="CreateBarnForm-successToastMessage">
          Successfully Created &quot;<span className="CreateBarnForm-successToastBarnName">{barnName}</span>&quot;!
        </span>
        <Button
          className="CreateBarnForm-setupButton"
          content={`Set up now`}
          variant="vivid"
          color="success"
          linkTo={`/barns/${barnId}/settings`}
        />
      </div>,
      {
        position: 'top-right',
        autoClose: false,
        closeOnClick: true,
      },
    );

  const handleOnError = (e) => {
    toast.error('Error Occurred', {
      position: 'top-right',
      autoClose: 8000,
      hideProgressBar: false,
      closeOnClick: true,
      pauseOnHover: true,
      draggable: true,
      progress: undefined,
    });

    Sentry?.captureException(e);

    if (e instanceof Error) {
      throw e;
    } else {
      throw new Error(e);
    }
  };

  return (
    <div className={`CreateBarnForm ${className}`}>
      <Formik
        initialValues={{
          name: '',
        }}
        onSubmit={async (values, formikBag) => {
          // save by sending the mutation then fire callbacks
          await saveBarn({
            variables: {
              newRowObject: {
                name: values.name,
                organization_id: orgId,
              },
            },
            onCompleted: (data) => {
              if (!data?.new_farm) {
                // if nothing is returned for this request it must have errored
                handleOnError('No new barn was created'); // toast
              } else {
                // Setting the value for the field in case they differ.
                formikBag.setValues({ name: data?.new_farm?.name });

                // Call refetch if provided
                refetch?.();
                handleOnSuccess(data?.new_farm?.name, data?.new_farm?.id); // toast
                setShowSuccess(true);
              }
            },
            onError: (error) => {
              console.error('Error creating barn', error);
              // TODO: (future, minor). We could display an error below the field but we'll see if that's more useful than the toast
              handleOnError(error); // toast
            },
          });
        }}
      >
        {({ isSubmitting, errors }) => (
          <Form className="CreateBarnForm--form">
            <LabelledColumn heading="  " className="CreateBarnForm-labelledColumn">
              <div style={{ display: 'flex', flexDirection: 'columns' }}>
                <Field
                  as={TextInput}
                  errors={errors?.name}
                  name="name"
                  placeholder="New Barn Name"
                  disabled={isSubmitting || savingBarn}
                  inputClassName="CreateBarnForm-textInput"
                  right={
                    <Button
                      disabled={isSubmitting || showSuccess}
                      type="submit"
                      variant="vivid"
                      color="success"
                      content={
                        showSuccess ? (
                          <CircledCheckmarkIcon className="CreateBarnForm-checkmark" />
                        ) : error ? (
                          '❌'
                        ) : (
                          'Create'
                        )
                      }
                      className="CreateBarnForm-saveButton"
                    />
                  }
                />
              </div>
            </LabelledColumn>
          </Form>
        )}
      </Formik>
    </div>
  );
}

CreateBarnForm.propTypes = {
  className: PropTypes.string,
  orgId: PropTypes.string.isRequired,
  refetch: PropTypes.func.isRequired,
};
